import { IEmoji, IEmojisCategory, SkinTone, EmojiVariant } from '@quicktools/emojis';
import { matchSorter } from 'match-sorter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

import useDefaultDeviceType from '../../hooks/defaultDeviceType';
import analytics from '../../services/analytics';
import useEmojiStore from '../../store/emojis-store';

function getName(category: string) {
  return category === 'index' ? 'main' : category;
}

function useApp(emojis: IEmojisCategory[]) {
  const cachedSearchTerm = useEmojiStore((state) => state.searchTerm);
  const [searchTerm, setSearchTerm] = useState(() => cachedSearchTerm || '');
  const [skinTone, setSkinTone] = useState(0);
  const [displaySkintone, setDisplaySkintone] = useState(true);
  const [searchFound, setSearchFound] = useState(null);
  const defaultDeviceType = useDefaultDeviceType();

  const [currentEmojis, setCurrentEmojis] = useState<IEmojisCategory[]>(() => [...emojis]);
  const debouncedSearchTerm = useDebounce(searchTerm, 700);

  const [emojiDeviceType, setEmojiDeviceType] = useState<EmojiVariant>('default');

  const toggleModal = useEmojiStore((state) => state.toggleModal);
  const showModal = useEmojiStore((state) => state.showModal);
  const chooseEmoji = useEmojiStore((state) => state.addToEditor);
  const setSearchValue = useEmojiStore((state) => state.setSearchTerm);

  const setCopyCardExpanded = useEmojiStore((state) => state.setEditorExpanded);

  const onChangeSearchText = useCallback(
    (searchText: string) => {
      setSearchTerm(searchText);
      setSearchValue(searchText);
      analytics.textBoxType();
    },
    [setSearchValue],
  );

  const onChangeCategory = useCallback((category, _, page) => {
    analytics.gaEvent('category_select', {
      category_name: getName(category),
      page_name: getName(page),
    });
  }, []);

  const onChangeEmojiDeviceType = useCallback((type: EmojiVariant) => {
    setEmojiDeviceType(type);
    // TBD Grigor
    // analytics.deviceTypeSwitch(type, type);
  }, []);

  const onToggleFilterModal = useCallback(() => {
    toggleModal();
  }, [toggleModal]);

  const onChooseEmoji = useCallback(
    (emoji: IEmoji) => {
      chooseEmoji(emoji.value);
      setCopyCardExpanded(true);
    },
    [chooseEmoji, setCopyCardExpanded],
  );

  const onChangeSkintone = useCallback((skin: SkinTone) => {
    setSkinTone(skin);
    analytics.pageClick({
      page_path: location.pathname,
      action_type: `skin_tone_${skin}`,
    });
  }, []);

  useEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setCurrentEmojis(() => {
      if (debouncedSearchTerm === '') {
        return emojis.map((items) => ({
          ...items,
          emojis: items.emojis.filter((emoji) => emoji.skinToneValues?.includes(skinTone)),
        }));
      }

      return emojis.map((items) => ({
        ...items,
        emojis: matchSorter([...items.emojis], debouncedSearchTerm, {
          keys: ['name', 'keywords', 'slug'],
          threshold: matchSorter.rankings.CONTAINS,
        }).filter((emoji) => {
          if (emoji.skinToneSupport) {
            return emoji.skinToneValues?.includes(skinTone);
          }
          if (emojiDeviceType === 'default') {
            if (!emoji.missing?.includes(defaultDeviceType)) {
              return emoji;
            }
          } else if (!emoji.missing?.includes(emojiDeviceType)) {
            return emoji;
          }
        }),
      }));
    });
  }, [debouncedSearchTerm, emojis, skinTone, emojiDeviceType]);

  useEffect(() => {
    const foundItems = currentEmojis.map((category) => category.emojis).flat();

    setSearchFound(searchTerm === '' ? null : foundItems.length);

    setDisplaySkintone(
      new Set(foundItems.map(({ skinToneSupport }) => skinToneSupport).flat()).has(true),
    );
  }, [currentEmojis]);

  return useMemo(
    () => ({
      skinTone,
      showModal,
      searchFound,
      currentEmojis,
      onChooseEmoji,
      emojiDeviceType,
      displaySkintone,
      onChangeCategory,
      onChangeSkintone,
      onChangeSearchText,
      onToggleFilterModal,
      debouncedSearchTerm,
      onChangeEmojiDeviceType,
      defaultDeviceType,
    }),
    [
      skinTone,
      showModal,
      searchFound,
      currentEmojis,
      onChooseEmoji,
      emojiDeviceType,
      displaySkintone,
      onChangeCategory,
      onChangeSkintone,
      onChangeSearchText,
      onToggleFilterModal,
      debouncedSearchTerm,
      onChangeEmojiDeviceType,
      defaultDeviceType,
    ],
  );
}

export default useApp;
